import { useCallback, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../../firebase-client";
import { functions } from "../../../firebase-client";
import { httpsCallable } from "firebase/functions";
import {
  isExternalPaymentSetupReady,
  MEMBERSHIP_HANDLING_MODES,
} from "../../../utils/membershipHandling";

const INITIAL_STATE = {
  loading: false,
  paid: false,
  hasExistingMembership: false,
  needsBillingAttention: false,
  error: "",
  membershipMode: MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE,
  billingCardReady: false,
  externalPaymentReady: false,
  membershipChoiceAvailable: false,
  membershipChoiceConfirmed: true,
  committeeReady: true,
  committee: [],
  isPrelaunchCommittee: false,
};

export default function useClubLaunchReadiness(teamId, enabled = true) {
  const [state, setState] = useState(INITIAL_STATE);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  useEffect(() => {
    if (!enabled || !teamId) {
      setState(INITIAL_STATE);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setState((current) => ({ ...current, loading: true, error: "" }));
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("You must be signed in to continue.");

        let committeeResponse = { data: { ready: true, members: [] } };
        try {
          committeeResponse = await httpsCallable(functions, "getPrelaunchCommitteeReadiness")({ teamId });
        } catch (_) {
          // Compatibility fallback while the callable is being deployed.
        }
        const committeeReady = committeeResponse?.data?.ready !== false;
        const committee = Array.isArray(committeeResponse?.data?.members) ? committeeResponse.data.members : [];

        const settingsSnap = await getDoc(doc(db, "users", user.uid, "teamSettings", teamId));
        const membershipMode = committeeResponse?.data?.membershipMode || MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE;
        const membershipChoiceAvailable = committeeResponse?.data?.membershipChoiceAvailable === true;
        const membershipChoiceConfirmed = committeeResponse?.data?.membershipChoiceConfirmed !== false;
        const settings = settingsSnap.exists() ? settingsSnap.data() || {} : {};
        const paymentMode = String(settings.paymentMode || "").trim().toLowerCase();
        const paymentStatus = String(settings.status || "").trim().toLowerCase();
        let membershipReady = paymentMode === "subscription" && (!paymentStatus || paymentStatus === "active");
        if (committeeResponse.data?.launchingMemberPrepared === true) membershipReady = true;
        if (!membershipReady && [MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE, MEMBERSHIP_HANDLING_MODES.HYBRID].includes(membershipMode)) {
          const today = new Date();
          const billingCycle = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
          const billingSnap = await getDoc(doc(db, "teams", teamId, "billingCycles", billingCycle, "members", user.uid));
          membershipReady = billingSnap.exists() && billingSnap.data()?.paid === true;
        }
        if (membershipMode !== MEMBERSHIP_HANDLING_MODES.HOSTED_STRIPE) {
          const [overview, setupSnap] = await Promise.all([
            httpsCallable(functions, "getClubBillingOverview")({ teamId }),
            getDoc(doc(db, "teams", teamId, "stripe", "setup")),
          ]);
          const billingCardReady = overview.data?.card?.healthy === true;
          const setup = setupSnap.data() || {};
          const externalPaymentReady = isExternalPaymentSetupReady(setup)
            && (membershipMode !== MEMBERSHIP_HANDLING_MODES.HYBRID
              || (setup.externalPaymentDisplayMode === 'price_and_instructions'
                && Boolean(String(setup.externalPaymentInstructions || '').trim())));
          if (!cancelled)
            setState({
              loading: false,
              paid: billingCardReady && (membershipMode !== MEMBERSHIP_HANDLING_MODES.HYBRID || membershipReady),
              hasExistingMembership: Boolean(paymentMode) || committeeResponse.data?.launchingMemberPrepared === true,
              needsBillingAttention: !billingCardReady,
              error: "",
              membershipMode,
              billingCardReady,
              externalPaymentReady,
              membershipChoiceAvailable,
              membershipChoiceConfirmed,
              committeeReady,
              committee,
              isPrelaunchCommittee: committeeResponse.data?.launchingMemberIsCommittee === true,
            });
          return;
        }
        if (!cancelled) {
          setState({
            loading: false,
            paid: membershipReady,
            hasExistingMembership: Boolean(paymentMode),
            needsBillingAttention:
              paymentMode === "subscription" &&
              Boolean(paymentStatus) &&
              paymentStatus !== "active",
            error: "",
            membershipMode,
            billingCardReady: false,
            externalPaymentReady: false,
            membershipChoiceAvailable,
            membershipChoiceConfirmed,
            committeeReady,
            committee,
            isPrelaunchCommittee: committeeResponse.data?.launchingMemberIsCommittee === true,
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            ...INITIAL_STATE,
            error: error?.message || "Unable to check membership readiness.",
          });
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshKey, teamId]);

  return { ...state, refresh };
}
